import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

// Create an axios instance
const api = axios.create({
  baseURL: "https://attendanceapi.gensparc.com/task",
});

// Add interceptor to include the JWT token from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Helper function to convert a date string to YYYY-MM-DD format
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date)) return "";
  const year = date.getFullYear();
  let month = (date.getMonth() + 1).toString();
  if (month.length < 2) month = "0" + month;
  let day = date.getDate().toString();
  if (day.length < 2) day = "0" + day;
  return `${year}-${month}-${day}`;
};

const Task = () => {
  const navigate = useNavigate();
  const [employeeId, setEmployeeId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const tasksPerPage = 5; // Adjust as needed

  // State for the update form dialog
  const [editingTask, setEditingTask] = useState(null);
  const [formValues, setFormValues] = useState({
    task_name: "",
    task_type: "",
    occurrence: "",
    start_date: "",
    end_date: "",
    task_description: "",
    status: "",
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });

// Web Push Notification

useEffect(() => {
  async function initializePushNotifications() {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      console.warn("Push notifications are not supported by the browser.");
      return;
    }

    try {
      // Step 1: Register the service worker
      const registration = await navigator.serviceWorker.register("/service-worker.js");
      console.log("Service Worker registered:", registration);

      // Step 2: Wait until the service worker is ready
      const readyRegistration = await navigator.serviceWorker.ready;
      console.log("Service Worker ready:", readyRegistration);

      // Step 3: Ask for notification permission
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        alert("Notification permission denied");
        return;
      }

      // Step 4: Unsubscribe previous if exists
      const existingSubscription = await readyRegistration.pushManager.getSubscription();
      if (existingSubscription) {
        await existingSubscription.unsubscribe();
      }

      // Step 5: Subscribe
      const subscribeOptions = {
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array("BDc3h3RL8hnhVkY7UcwBz_xVGaguTf5qb8EdP01glAN8GAipmf1iI9PIZjgjV_BSbS28crmteBiF4cHZZYqRFfg")
      };

      const subscription = await readyRegistration.pushManager.subscribe(subscribeOptions);
      console.log("New Push Subscription:", subscription);

      const token = localStorage.getItem("authToken");
      const decodedToken = jwtDecode(token);
      const employee_id = decodedToken.userId;
     const user_type = decodedToken.user_type;

      const response = await fetch("https://attendanceapi.gensparc.com/task/save-subscription", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ subscription, employee_id, user_type: user_type, }),
      });

      const result = await response.json();
      console.log("Subscription saved:", result);
    } catch (err) {
      console.error("Push notification setup failed:", err);
    }
  }

  initializePushNotifications();

  function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = atob(base64);
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
  }
}, []);





  // Decode the JWT token to get the user/employee id
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      const decodedToken = jwtDecode(token);
      setEmployeeId(decodedToken.userId);
      console.log("Employee ID from token:", decodedToken.userId);
    } else {
      console.error("No auth token found");
    }
  }, []);

  // Fetch tasks for the logged-in employee
  const fetchTasks = useCallback(async () => {
    if (!employeeId) {
      console.error("Employee ID is not provided.");
      return;
    }
    try {
      setLoading(true);
      console.log("Fetching tasks for employee:", employeeId);
      const response = await api.get(`/employee-tasks/${employeeId}`);
      setTasks(response.data);
      console.log("Tasks received:", response.data);
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setError(err.response?.data?.message || "Error fetching tasks.");
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (employeeId) {
      fetchTasks();
    }
  }, [fetchTasks, employeeId]);

  // Calculate pagination indices
  const indexOfLastTask = currentPage * tasksPerPage;
  const indexOfFirstTask = indexOfLastTask - tasksPerPage;
  const currentTasks = tasks.slice(indexOfFirstTask, indexOfLastTask);

  // Open the update form dialog and prefill form values including formatted dates
  const handleEditClick = (task) => {
    setEditingTask(task);
    setFormValues({
      task_name: task.task_name || "",
      task_type: task.task_type || "",
      occurrence: task.occurrence || "",
      start_date: formatDate(task.start_date), // Auto-filled and formatted date
      end_date: formatDate(task.end_date),     // Auto-filled and formatted date
      task_description: task.task_description || "",
      status: task.status || "",
    });
  };

  // Handle changes in the form inputs
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({ ...prevValues, [name]: value }));
  };

  // Submit the update form
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!editingTask) return;
    try {
      await api.put(`/update/${editingTask.task_id}`, formValues);
      setSuccess("Task updated successfully.");
      setEditingTask(null);
      fetchTasks();
    } catch (err) {
      console.error("Failed to update task:", err);
      setError(err.response?.data?.message || "Failed to update task.");
    }
  };

  const handleDialogClose = () => {
    setEditingTask(null);
  };

  // Navigate back to the dashboard
  const handleBack = () => {
    navigate("/dashboard");
  };

  const commonStyles = {
    fontWeight: "bold",
    fontSize: "12px",
    whiteSpace: "nowrap",
    padding: "8px",
    textAlign: "center",
  };

  const containerMaxWidth = isMobile ? "xs" : "md";

  return (
    <Container maxWidth={containerMaxWidth}>
      <Paper elevation={4} sx={{ p: 4, my: 4, borderRadius: 2 }}>
      <Typography variant="h5" gutterBottom>
        <IconButton onClick={handleBack} sx={{ mb: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        {/* <Typography variant="h5" gutterBottom> */}
          My Tasks
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
            <CircularProgress />
          </Box>
        ) : tasks.length === 0 ? (
          <Typography>No tasks available.</Typography>
        ) : (
          <>
            {isMobile ? (
              // Mobile view using Accordions
              currentTasks.map((task) => (
                <Accordion key={task.task_id} sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography variant="subtitle1">
                        {task.task_name}
                      </Typography>
                      <Typography variant="caption">
                        Status: {task.status}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2">
                      <strong>Task ID:</strong> {task.task_id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Employee:</strong> {task.employee_id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Modified Time:</strong>{" "}
                      {task.modified_time || task.created_at}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        onClick={() => handleEditClick(task)}
                      >
                        Edit
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))
            ) : (
              // Desktop view using Table
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: "#f4f5f7" }}>
                    <TableRow>
                      <TableCell sx={commonStyles}>Task ID</TableCell>
                      <TableCell sx={commonStyles}>Task Name</TableCell>
                      <TableCell sx={commonStyles}>Employee</TableCell>
                      <TableCell sx={commonStyles}>Status</TableCell>
                      <TableCell sx={commonStyles}>Modified Time</TableCell>
                      <TableCell sx={commonStyles}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentTasks.map((task) => (
                      <TableRow key={task.task_id}>
                        <TableCell sx={commonStyles}>
                          {task.task_id}
                        </TableCell>
                        <TableCell sx={commonStyles}>
                          {task.task_name}
                        </TableCell>
                        <TableCell sx={commonStyles}>
                          {task.employee_id}
                        </TableCell>
                        <TableCell sx={commonStyles}>
                          {task.status}
                        </TableCell>
                        <TableCell sx={commonStyles}>
                          {task.modified_time || task.created_at}
                        </TableCell>
                        <TableCell sx={commonStyles}>
                          <Button
                            variant="contained"
                            onClick={() => handleEditClick(task)}
                          >
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {tasks.length > tasksPerPage && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={Math.ceil(tasks.length / tasksPerPage)}
                  page={currentPage}
                  onChange={(event, value) => setCurrentPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Update Form Dialog */}
      <Dialog
        open={!!editingTask}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
    <IconButton onClick={handleDialogClose} sx={{ mr: 1 }}>
      <ArrowBackIcon />
    </IconButton>
    Update Task
  </DialogTitle>
        <form onSubmit={handleFormSubmit}>
          <DialogContent>
            <TextField
              margin="dense"
              label="Task Name"
              name="task_name"
              value={formValues.task_name}
              onChange={handleFormChange}
              fullWidth
              required
            />
            <TextField
              margin="dense"
              label="Task Type"
              name="task_type"
              value={formValues.task_type}
              onChange={handleFormChange}
              fullWidth
              required
            />
            <TextField
              margin="dense"
              label="Occurrence"
              name="occurrence"
              value={formValues.occurrence}
              onChange={handleFormChange}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Start Date"
              name="start_date"
              type="date"
              value={formValues.start_date}
              onChange={handleFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              margin="dense"
              label="End Date"
              name="end_date"
              type="date"
              value={formValues.end_date}
              onChange={handleFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              margin="dense"
              label="Task Description"
              name="task_description"
              value={formValues.task_description}
              onChange={handleFormChange}
              fullWidth
              multiline
              rows={3}
              required
            />
            <FormControl fullWidth margin="dense" required>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formValues.status}
                onChange={handleFormChange}
                label="Status"
              >
                <MenuItem value="Start">Start</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              Update Task
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default Task;
